import axios, { AxiosInstance } from 'axios';
import config from '../../../config';
import type { Conversation, ChatMessage, RoleType } from '../../../types/skubot';
import type { PresentationChatMessage, PresentationChatRequest, PresentationChatResponse } from '../../../types/presentationBuilderChat';
import { generateUUID, getAccessToken } from '../../../utils';

const API_BASE_URL = config.skubot.apiBaseUrl.startsWith('http') ? config.skubot.apiBaseUrl : (
  `${window.location.origin}${config.skubot.apiBaseUrl.startsWith('/') ? '' : '/'}${config.skubot.apiBaseUrl}`
);

export function buildContent(data: PresentationChatResponse): string {
  switch (data.status) {
      case 'finished': {
          const n = data.num_products_added;
          return `Added ${n} ${n === 1 ? 'product' : 'products'} to the presentation.`;
      }
      case 'awaiting_input':
          return data.message ?? '';
      case 'failed':
          return 'No products could be added for that request.';
      default:
          return 'Something went wrong.';
  }
}

function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    if (!config.headers?.has('Authorization')) {
      const accessToken = getAccessToken();
      if (accessToken) {
        config.headers.set('Authorization', `Bearer ${accessToken}`);
      }
    }
    if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers.set('X-CSRF-Token', csrfToken);
      }
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 401) {
        return Promise.reject(
          new Error(detail ?? 'Your session could not be verified. Please refresh the page and try again.')
        );
      }
      if (status === 403) {
        return Promise.reject(
          new Error(detail ?? `You don't have access to this action. Contact your admin if you think this is a mistake.'`)
        );
      }
      if (status === 500) {
        return Promise.reject(
          new Error(detail ?? "skubot's response could not be fully generated.")
        );
      }
      
      return Promise.reject(error);
    }
  );

  return client;
}

const api = createApiClient();

type ConversationResponse = {
  uuid: string
  session_id: string
  title: string | null
  status: string
  created_at: string
  updated_at: string
};

type ConversationsResponse = {
  conversations: ConversationResponse[];
};

type ChatMessagesResponse = {
  messages: {
    id: string;
    role: RoleType;
    content: string;
    trace_id: string | null;
    created_at: string;
  }[];
};

type ChatResponseMetadata = {
  status: boolean
  trace_id: string | null
  session: string
}

type ChatMessageResponse = {
  role: RoleType
  content: string
  metadata: ChatResponseMetadata
}

type SessionResponse = {
  authenticated: boolean;
  user: { user_id: string };
};

export const skubotApi = {
  getSession: async (): Promise<SessionResponse> => {
    const response = await api.get<SessionResponse>('/auth/session');
    return response.data;
  },
  getConversations: async (): Promise<Conversation[]> => {
    const response = await api.get<ConversationsResponse>('/api/conversations');
    return response.data.conversations.map((conversation) => ({
      id: conversation.uuid,
      sessionId: conversation.session_id,
      title: conversation.title ?? 'New Conversation',
      createdAt: conversation.created_at,
      updatedAt: conversation.updated_at,
    }));
  },
  generateSessionId: async (): Promise<string> => {
    const response = await api.post<{ session_id: string }>('/api/conversations/generate-session-id');
    return response.data.session_id;
  },
  updateConversationTitle: async (conversationId: string, title: string): Promise<Conversation> => {
    const response = await api.patch<ConversationResponse>(`/api/conversations/${conversationId}/title`, { title });
    const data = response.data;
    return {
      id: data.uuid,
      sessionId: data.session_id,
      title: data.title ?? 'New Conversation',
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },
  deleteConversation: async (conversationId: string): Promise<void> => {
    await api.delete<void>(`/api/conversations/${conversationId}`);
  },
  getConversationMessages: async (conversationId: string): Promise<ChatMessage[]> => {
    const response = await api.get<ChatMessagesResponse>(`/api/conversations/${conversationId}/messages`);
    return response.data.messages.map((message) => ({
      id: message.id,
      conversationId: conversationId,
      role: message.role,
      content: message.content,
      traceId: message.trace_id,
      createdAt: message.created_at,
    }));
  },
  chat: async (conversationId: string, message: string, tempMessageId?: string): Promise<ChatMessage> => {
    const response = await api.post<ChatMessageResponse>(
      '/api/chat',
      { session: conversationId, role: 'user', content: message }
    );
    const data = response.data;
    const id = tempMessageId ?? generateUUID();

    return {
      id,
      conversationId: conversationId,
      role: data.role,
      content: data.content,
      createdAt: new Date().toISOString(),
      traceId: data.metadata?.trace_id ?? null,
    };
  },
  presentationChat: async (
    request: PresentationChatRequest,
  ): Promise<PresentationChatMessage> => {
    const response = await api.post<PresentationChatResponse>('/api/presentation/add-product', request);
    const data = response.data;

    return {
      id: data.message_id,
      conversationId: request.session_id,
      numProductsAdded: data.num_products_added,
      status: data.status,
      content: buildContent(data),
      createdAt: new Date().toISOString(),
      role: 'assistant',
    };
  },
  submitFeedback: async (
    traceId: string,
    rating: 'up' | 'down',
    comment?: string,
  ): Promise<void> => {
    const body: {
      name: string,
      value: number,
      trace_id: string,
      data_type: string,
      comment?: string,
    } = {
      name: "user_feedback",
      value: rating === 'up' ? 1 : 0,
      trace_id: traceId,
      data_type: "NUMERIC",
      comment: comment,
    };
    await api.post<void>(`/api/feedback`, body);
  },
};
